import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";
import { canManageTournament } from "../utils/authorization";
import {
  RESULTS_CACHE_TABLE,
  RESULTS_CACHE_POLL_MS,
  RESULTS_CACHE_UPDATE_TIMEOUT_MS,
  RESULTS_CACHE_UPDATE_TIMEOUT_MESSAGE,
  getUsableCachePayload,
  isReadyCacheHit,
  isResultsCacheDebugEnabled,
  isResultsCacheUpdating,
  logResultsCacheDebug,
  resolveResultsUpdateTimeout,
  resultsCacheFingerprint,
  resultsCacheRequestKey,
  shouldInvokeProcessTournamentResults,
} from "../utils/resultsCache";

const inflightResultsRequests = new Map();

const overlayViewerFields = async (payload, tournamentId, user) => {
  const { data: verificationStates } = await supabase
    .from("verificationStates")
    .select("entityType, isVerified")
    .eq("tournamentId", tournamentId)
    .in("entityType", ["payouts", "results"]);

  const payoutsVerified = Boolean(
    verificationStates?.find((row) => row.entityType === "payouts")?.isVerified
  );
  const resultsVerified = Boolean(
    verificationStates?.find((row) => row.entityType === "results")?.isVerified
  );

  let canManage = false;
  let userRole = null;
  if (user?.id) {
    const permission = await canManageTournament(user.id, tournamentId);
    canManage = Boolean(permission.canManage);
    userRole = permission.userRole || null;
  }

  return {
    ...payload,
    userPermissions: { canManage, userRole },
    canPreview: payoutsVerified && canManage,
    canFinalize: payoutsVerified && canManage && !resultsVerified,
    tournament: {
      ...(payload.tournament || {}),
      isResultsFinalized: resultsVerified,
      isPayoutFinalized: payoutsVerified,
    },
  };
};

const emptyCacheRead = {
  payload: null,
  isUpdating: false,
  isReadyHit: false,
  errorMessage: null,
};

const readResultsCache = async (
  tournamentId,
  eventId,
  showPayouts,
  cacheOnly
) => {
  const debug = isResultsCacheDebugEnabled();
  const { data: tournament, error: tournamentError } = await supabase
    .from("tournaments")
    .select("resultsVersion")
    .eq("id", tournamentId)
    .maybeSingle();

  if (tournamentError || tournament?.resultsVersion == null) {
    logResultsCacheDebug("miss: no resultsVersion (treat as recompute)", {
      tournamentId,
      error: tournamentError?.message,
    });
    return emptyCacheRead;
  }

  const expectedFingerprint = resultsCacheFingerprint(
    tournament.resultsVersion,
    showPayouts
  );
  if (!expectedFingerprint) {
    logResultsCacheDebug("miss: could not build fingerprint", {
      tournamentId,
      resultsVersion: tournament.resultsVersion,
      showPayouts,
    });
    return emptyCacheRead;
  }

  let query = supabase
    .from(RESULTS_CACHE_TABLE)
    .select("payload, status, inputFingerprint, errorMessage")
    .eq("tournamentID", tournamentId)
    .eq("includePayouts", showPayouts);

  query = eventId ? query.eq("eventID", eventId) : query.is("eventID", null);

  const { data: row, error: cacheError } = await query.maybeSingle();
  if (cacheError) {
    logResultsCacheDebug("miss: cache query error", {
      tournamentId,
      expectedFingerprint,
      error: cacheError.message,
    });
    return emptyCacheRead;
  }

  const payload = getUsableCachePayload(row);
  const isUpdating = isResultsCacheUpdating(row, expectedFingerprint, {
    treatMissAsUpdating: cacheOnly,
  });
  const hit = isReadyCacheHit(row, expectedFingerprint);
  if (debug) {
    logResultsCacheDebug(
      hit ? "HIT (no edge call)" : payload ? "STALE (show last copy)" : "miss",
      {
        tournamentId,
        showPayouts,
        eventId: eventId || null,
        resultsVersion: tournament.resultsVersion,
        expectedFingerprint,
        storedFingerprint: row?.inputFingerprint ?? null,
        storedStatus: row?.status ?? null,
        hadRow: Boolean(row),
        isUpdating,
        cacheErrorMessage: row?.errorMessage ?? null,
      }
    );
  }

  return {
    payload,
    isUpdating,
    isReadyHit: hit,
    errorMessage: row?.status === "error" ? row.errorMessage : null,
  };
};

const invokeProcessTournamentResults = async (
  tournamentId,
  eventId,
  showPayouts,
  forceRefresh
) => {
  const { data: results, error: resultsError } =
    await supabase.functions.invoke("process-tournament-results", {
      body: {
        tournamentId,
        eventId,
        showPayouts,
        forceRefresh,
      },
    });

  if (resultsError) {
    throw new Error(
      `Failed to fetch tournament results: ${resultsError.message}`
    );
  }

  if (!results?.success) {
    throw new Error(results?.error || "Failed to process tournament results");
  }

  return results;
};

const EMPTY_CACHE_PAYLOAD = {
  success: true,
  results: [],
  payouts: null,
  showPayouts: false,
};

const EMPTY_RESULTS = [];

const applyCacheUpdatingState = (
  cached,
  updateTimedOutRef,
  setIsUpdating,
  setUpdateError
) => {
  if (cached.isReadyHit) {
    updateTimedOutRef.current = false;
    setIsUpdating(false);
    setUpdateError(null);
    return;
  }
  if (updateTimedOutRef.current && cached.isUpdating) {
    setIsUpdating(false);
    return;
  }
  setIsUpdating(cached.isUpdating);
  if (!cached.isUpdating) {
    setUpdateError(cached.errorMessage || null);
  }
};

export const useTournamentResultsEnhanced = (
  tournamentId,
  eventId = null,
  showPayouts = false,
  options = {}
) => {
  const cacheOnly = Boolean(options.cacheOnly);
  const [rawData, setRawData] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const updatingStartedAtRef = useRef(null);
  const updateTimedOutRef = useRef(false);

  const fetchTournamentResults = useCallback(
    async (forceRefresh = false, options = {}) => {
      if (!tournamentId) {
        setLoading(false);
        return;
      }

      const silent = Boolean(options.silent);
      const requestKey = resultsCacheRequestKey(
        tournamentId,
        eventId,
        showPayouts,
        cacheOnly
      );

      try {
        if (!silent) {
          setLoading(true);
        }
        setError(null);
        if (forceRefresh) {
          updateTimedOutRef.current = false;
          setUpdateError(null);
        }

        const run = async () => {
          const cached = forceRefresh
            ? emptyCacheRead
            : await readResultsCache(
                tournamentId,
                eventId,
                showPayouts,
                cacheOnly
              );

          const invoke = shouldInvokeProcessTournamentResults({
            cacheOnly,
            forceRefresh,
            isReadyHit: cached.isReadyHit,
          });

          if (cached.payload && !invoke) {
            applyCacheUpdatingState(
              cached,
              updateTimedOutRef,
              setIsUpdating,
              setUpdateError
            );
            return cached.payload;
          }

          if (!invoke) {
            logResultsCacheDebug("cacheOnly miss: skip edge call", {
              tournamentId,
              showPayouts,
              eventId: eventId || null,
              isUpdating: cached.isUpdating,
              errorMessage: cached.errorMessage,
            });
            applyCacheUpdatingState(
              cached,
              updateTimedOutRef,
              setIsUpdating,
              setUpdateError
            );
            return cached.payload || EMPTY_CACHE_PAYLOAD;
          }

          if (forceRefresh) {
            logResultsCacheDebug("forceRefresh: skip cache, invoke", {
              tournamentId,
              showPayouts,
              eventId: eventId || null,
            });
          } else {
            logResultsCacheDebug("invoking process-tournament-results", {
              tournamentId,
              showPayouts,
              eventId: eventId || null,
              forceRefresh: false,
            });
          }
          setIsUpdating(false);
          setUpdateError(null);
          return invokeProcessTournamentResults(
            tournamentId,
            eventId,
            showPayouts,
            forceRefresh
          );
        };

        let pending = inflightResultsRequests.get(requestKey);
        if (!pending || forceRefresh) {
          pending = run().finally(() => {
            inflightResultsRequests.delete(requestKey);
          });
          inflightResultsRequests.set(requestKey, pending);
        }

        const payload = await pending;
        const overlaid = await overlayViewerFields(
          payload,
          tournamentId,
          userRef.current
        );
        setRawData(payload);
        setData(overlaid);
      } catch (err) {
        console.error("Error fetching tournament results:", err);
        setError(err.message);
        setRawData(null);
        setData(null);
        setIsUpdating(false);
        setUpdateError(null);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [tournamentId, eventId, showPayouts, cacheOnly]
  );

  useEffect(() => {
    fetchTournamentResults(false);
  }, [fetchTournamentResults]);

  useEffect(() => {
    if (!isUpdating || !tournamentId) {
      updatingStartedAtRef.current = null;
      return undefined;
    }

    if (updatingStartedAtRef.current == null) {
      updatingStartedAtRef.current = Date.now();
    }

    const intervalId = setInterval(() => {
      fetchTournamentResults(false, { silent: true });
    }, RESULTS_CACHE_POLL_MS);

    return () => clearInterval(intervalId);
  }, [isUpdating, tournamentId, fetchTournamentResults]);

  useEffect(() => {
    if (!isUpdating || !tournamentId) {
      return undefined;
    }

    if (updatingStartedAtRef.current == null) {
      updatingStartedAtRef.current = Date.now();
    }
    const startedAt = updatingStartedAtRef.current;
    const delay = Math.max(
      0,
      RESULTS_CACHE_UPDATE_TIMEOUT_MS - (Date.now() - startedAt)
    );

    const timeoutId = setTimeout(() => {
      const { timedOut, updateError: timeoutError } =
        resolveResultsUpdateTimeout({
          isUpdating: true,
          updatingStartedAt: startedAt,
          now: Date.now(),
          errorMessage: RESULTS_CACHE_UPDATE_TIMEOUT_MESSAGE,
        });
      if (!timedOut) {
        return;
      }
      console.warn("[results-cache] update timed out", {
        tournamentId,
        eventId,
        showPayouts,
        cacheOnly,
      });
      updateTimedOutRef.current = true;
      setIsUpdating(false);
      setUpdateError(timeoutError);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [isUpdating, tournamentId, eventId, showPayouts, cacheOnly]);

  useEffect(() => {
    let cancelled = false;

    const applyOverlay = async () => {
      if (!rawData) {
        setData(null);
        return;
      }

      const overlaid = await overlayViewerFields(
        rawData,
        tournamentId,
        user
      );
      if (!cancelled) {
        setData(overlaid);
      }
    };

    applyOverlay();
    return () => {
      cancelled = true;
    };
  }, [rawData, tournamentId, user]);

  const refetch = useCallback(() => {
    return fetchTournamentResults(true);
  }, [fetchTournamentResults]);

  return {
    data,
    loading,
    isUpdating,
    error,
    updateError,
    refetch,
    results: data?.results || EMPTY_RESULTS,
    payouts: data?.payouts || null,
    showPayouts: data?.showPayouts || false,
    calcMeta: data?.calcMeta || null,
    canPreview: data?.canPreview || false,
    canFinalize: data?.canFinalize || false,
    userPermissions: data?.userPermissions || {
      canManage: false,
      userRole: null,
    },
    tournament: data?.tournament || null,
  };
};
